const { getElement, getElements, parseInteger } = window.BAO.Utils

export class TradeFormValidation extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['message', 'submit']
  }

  static get observedAttributes () {
    return ['data-password-length']
  }

  setupListeners () {
    super.setupListeners()

    this.handlePasswordInput = this.handlePasswordInput.bind(this)
    this.passwordInputEls.forEach(passwordInputEl => {
      this.listeners.add(passwordInputEl, 'input', this.handlePasswordInput)
    })

    this.listeners.add(this, 'click', this.handleClick.bind(this))

    if (this.formEl) {
      this.listeners.add(
        this.formEl,
        'submit',
        this.handleFormSubmission.bind(this),
      )
    }

    this.requiredEls.forEach(requiredEl => {
      this.listeners.add(requiredEl, 'invalid', event => {
        event.preventDefault()
      })
    })
  }

  /**
   * @param {Event} event Click event triggered by clicking anywhere within the element
   */
  handleClick (event) {
    const submitEl = event.target.closest(this.els.submit.selector)
    if (submitEl) {
      this.handleFormSubmission(event)

      if (submitEl.getAttribute('aria-disabled') === 'true') {
        event.preventDefault()
        return false
      }
    }
  }

  /**
   * @param {Event} event Input event triggered on input of a password field
   */
  handlePasswordInput (event) {
    this.passwordIsValid = true

    /* Enable register button when passwords match */
    const formPasswordInputEl = getElement(
      '[data-form-validation-password-input]',
      { context: this },
    )
    const formConfirmPasswordInputEl = getElement(
      '[data-form-validation-confirm-password-input]',
      { context: this },
    )

    const validationEl = getElement(this.els.message.selector, {
      context: formConfirmPasswordInputEl.parentNode,
    })
    formConfirmPasswordInputEl.setAttribute('aria-invalid', 'false')
    this.toggleElement(validationEl, true)

    if (formPasswordInputEl.value !== formConfirmPasswordInputEl.value) {
      this.passwordIsValid = false
      if (formConfirmPasswordInputEl.value.length) {
        this.toggleElement(validationEl, false)
        formConfirmPasswordInputEl.setAttribute('aria-invalid', 'true')
      }
    }

    /* Check password requirements */
    const value = formPasswordInputEl.value
    const meetsLengthRequirement =
      value.length >= parseInteger(this.dataset?.passwordLength)
    const containsNumbers = value.replace(/\d/g, '') !== value
    const containsLetters = value.replace(/[A-Za-z]/g, '')
    const containsUpperCase = value.toLowerCase() !== value
    const containsLowerCase = value.toUpperCase() !== value

    const characterCountEl = this.els.requirement.elements.find(
      validationEl => validationEl.dataset?.validationType === 'character-count',
    )
    characterCountEl.setAttribute('aria-invalid', 'false')
    if (!characterCountEl || !meetsLengthRequirement) {
      characterCountEl.setAttribute('aria-invalid', 'true')
      this.passwordIsValid = false
    }

    const characterTypesEl = this.els.requirement.elements.find(
      validationEl => validationEl.dataset?.validationType === 'character-types',
    )
    characterTypesEl.setAttribute('aria-invalid', 'false')
    if (!characterTypesEl || !containsNumbers || !containsLetters) {
      characterTypesEl.setAttribute('aria-invalid', 'true')
      this.passwordIsValid = false
    }

    const characterCaseEl = this.els.requirement.elements.find(
      validationEl => validationEl.dataset?.validationType === 'character-case',
    )
    characterCaseEl.setAttribute('aria-invalid', 'false')
    if (!characterCaseEl || !containsUpperCase || !containsLowerCase) {
      characterCaseEl.setAttribute('aria-invalid', 'true')
      this.passwordIsValid = false
    }

    formPasswordInputEl.setAttribute(
      'aria-invalid',
      String(!this.passwordIsValid),
    )
    formConfirmPasswordInputEl.setAttribute(
      'aria-invalid',
      String(!this.passwordIsValid),
    )
  }

  /**
   * @param {Event} event Submit event triggered on submission of the form
   */
  handleFormSubmission (event) {
    const checkboxes = Array.from(
      document.querySelectorAll('.frm-Form_Checkbox'),
    )
    const returnedCheckboxes = checkboxes.reduce(
      (acc, curr) => acc || curr.checked,
      false,
    )

    const submitMessageEl = this.els?.['submit-message']?.element
    if (submitMessageEl) {
      submitMessageEl.textContent = ''
      this.toggleElement(submitMessageEl, true)
    }

    const invalidEl = this.requiredEls.find(
      requiredEl =>
        requiredEl.value.length < 1 ||
        (requiredEl.matches(this.passwordInputSelectors) &&
          requiredEl.getAttribute('aria-invalid') === 'true'),
    )
    this.requiredEls.forEach(requiredEl => {
      /* Ensure password fields are actually valid before changing them to valid */
      const isInvalid =
        requiredEl.matches(this.passwordInputSelectors) &&
        requiredEl.getAttribute('aria-invalid') === 'true'
      requiredEl.setAttribute('aria-invalid', String(isInvalid))
      this.toggleElement(
        getElement(this.els.message.selector, {
          context: requiredEl.parentNode,
        }),
        true,
      )
    })

    if (invalidEl || returnedCheckboxes === false) {
      this.log(invalidEl)
      event.preventDefault()

      document
        .querySelector("[data-trade-form-el='trade-form-services-required']")
        .setAttribute('aria-hidden', 'false')

      document
        .querySelector("[data-trade-form-el='submit-message-checkbox']")
        .setAttribute('aria-hidden', 'false')

      let invalidMessage = ''

      this.requiredEls.forEach(requiredEl => {
        if (requiredEl.type === 'radio') {
          const parentFieldset = requiredEl.closest('fieldset')
          const formData = new FormData(requiredEl.form)
          const hasValue = Boolean(formData.get(requiredEl.name))

          parentFieldset.setAttribute('aria-invalid', !hasValue)

          const validationEl = getElement(this.els.message.selector, {
            context: parentFieldset,
          })
          this.toggleElement(validationEl, false)

          if (validationEl) {
            invalidMessage += `${validationEl.textContent}<br />`
          }
        } else if (
          requiredEl.value.length < 1 ||
          requiredEl.getAttribute('aria-invalid') === 'true'
        ) {
          requiredEl.setAttribute('aria-invalid', 'true')
          const validationEl = getElement(this.els.message.selector, {
            context: requiredEl.parentNode,
          })
          this.toggleElement(validationEl, false)

          if (validationEl) {
            invalidMessage += `${validationEl.textContent}<br />`
          }
        }
      })

      if (submitMessageEl) {
        submitMessageEl.innerHTML = invalidMessage
        this.toggleElement(submitMessageEl, false)
      }
    }

    if (returnedCheckboxes === true) {
      document
        .querySelector("[data-trade-form-el='trade-form-services-required']")
        .setAttribute('aria-hidden', 'true')

      document
        .querySelector("[data-trade-form-el='submit-message-checkbox']")
        .setAttribute('aria-hidden', 'true')
    }
  }

  /**
   * @param {HTMLElement} element The HTMLElement we wish to show/hide
   * @param {boolean} isHidden Whether or not the provided element should be hidden
   */
  toggleElement (element, isHidden) {
    element?.setAttribute('aria-hidden', String(isHidden))
  }

  get formEl () {
    return getElement('form', { context: this })
  }

  get passwordInputEls () {
    return getElements(this.passwordInputSelectors, { context: this })
  }

  get passwordInputSelectors () {
    return '[data-form-validation-password-input], [data-form-validation-confirm-password-input]'
  }

  get invalidEls () {
    return getElements('[aria-invalid="true"]', { context: this })
  }

  get requiredEls () {
    return getElements('[required], [aria-required="true"]', { context: this })
  }
}

if (!customElements.get('trade-form-validation')) {
  customElements.define('trade-form-validation', TradeFormValidation)
}
